import { Box, Button, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import CancelDialogIcon from "@entrilia/shared/icons/CancelDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../../api/adminApi";
import type { FundraisingAccessItem } from "../../../../../../api/types/fundraisingTypes";

interface Props {
  editedAccessItem: FundraisingAccessItem;
  fundInvestorsWithContactCount: number;
  onSubmit: () => void;
  onClose: () => void;
}

const updateFundInvestor = withErrorHandling(adminApi.updateFundInvestor);

const RemoveFundraisingAccessDialog = ({
  editedAccessItem,
  fundInvestorsWithContactCount,
  onSubmit,
  onClose,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);

    const [, error] = await updateFundInvestor(editedAccessItem.fundInvestorId, {
      existingContacts: [],
      newContacts: [],
      removedContactIds: [editedAccessItem.contactId],
    });

    setIsSaving(false);

    if (error) {
      logError(error, "[RemoveFundraisingAccessDialog] updateFundInvestor");
      sendNotificationError(error.message);
      return;
    }

    onSubmit();
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Remove {editedAccessItem.contactName}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center", px: 1 }}>
          <CancelDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Remove contact?</Typography>
            <Box>
              <Typography component="span" color="textSecondary">
                You&apos;re about to remove{" "}
                <strong>
                  {editedAccessItem.contactName} &lt;{editedAccessItem.contactEmail}&gt;
                </strong>{" "}
                from investor <strong>{editedAccessItem.investorName}. </strong>
              </Typography>
              {fundInvestorsWithContactCount <= 1 && (
                <Typography component="span" color="textSecondary">
                  The contact will lose access to this fund.
                </Typography>
              )}
              {fundInvestorsWithContactCount > 1 && (
                <Typography component="span" color="textSecondary">
                  This contact will retain access to this fund via{" "}
                  <strong>
                    {fundInvestorsWithContactCount - 1} other{" "}
                    {fundInvestorsWithContactCount === 2 ? "investor" : "investors"}.
                  </strong>
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={isSaving} onClick={handleSave}>
          Remove
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default RemoveFundraisingAccessDialog;
